// angular
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
// other libraries
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerModule } from 'ngx-spinner';
// components
import { ChatOptionsComponent } from '../chat-options/chat-options.component';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { SuggestMessageBoxComponent } from '../suggest-message-box/suggest-message-box.component';
import { AlertBoxComponent } from '../alert-box/alert-box.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { PaginationOnScrollComponent } from '../pagination-on-scroll/pagination-on-scroll.component';
import { MessagesSkeletonComponent } from '../messages-skeleton/messages-skeleton.component';
import { PublicChatFormComponent } from '../public-chat-form/public-chat-form.component';
// services
import { UserService } from '@app/shared/services/user.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { ChatService } from '@app/+chat/shared/services/chat.service';
import { MessagingService } from '@app/+chat/shared/services/messaging.service';
import { StorageService } from '@app/shared/services/storage.service';
// interfaces & envs
import { IBusiness } from '@app/+suppliers/shared/interfaces/business.interface';
import { IMessage } from '@app/+chat/shared/interfaces';
import { AlertType } from '@app/+chat/shared/types/alert.type';
import { environment } from '@environments/environment';
// enums
import { AlertStatesEnum } from '@app/+chat/shared/enums/alert-states.enum';

@Component({
  selector: 'pwj-chat-messages-container',
  standalone: true,
  imports: [
    ChatOptionsComponent,
    MessageBoxComponent,
    SuggestMessageBoxComponent,
    AlertBoxComponent,
    NgxSkeletonLoaderModule,
    NgxSpinnerModule,
    ChatInputComponent,
    PaginationOnScrollComponent,
    MessagesSkeletonComponent,
    PublicChatFormComponent,
  ],
  templateUrl: './chat-messages-container.component.html',
  styleUrl: './chat-messages-container.component.scss',
})
export class ChatMessagesContainerComponent implements OnInit, AfterViewInit {
  // chat state properties
  public alertState!: AlertStatesEnum;
  // chat type properties
  // start support chat
  public startChatAlertData: AlertType = {
    firstButtonText: 'chat.chat',
    firstButtonAction: () => {
      this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
      const isChatAlreadyStarted = this._storageService.getStartedSupportChat();
      // save selection in local storage
      if (!isChatAlreadyStarted) {
        this._storageService.setStartedSupportChat();
      }
      this.startChatWithSupport(this._chatId);
    },
  };
  // messages data properties
  public messages: IMessage[] = [];
  public messagesCount!: number;
  public suggestionMessages: string[] = [];
  public isMessagesLoading!: boolean;

  // pagination state
  public showPagination: boolean = true;
  // last page height
  public lastPageHeight: number = 0;

  // chat data properties
  public isSupportChatMode!: boolean;
  public isChatLoading!: boolean;
  public businessChatData!: IBusiness;

  // private properties
  private _businessId!: string;
  private _token!: string;
  private _kid!: string;
  private _isLoggedIn: boolean = false;
  private _chatId!: string;

  // get message container ref
  @ViewChild('messagesContainer', { static: false })
  public messagesContainer!: ElementRef;

  // get messages ref
  @ViewChildren('message') public messageElement!: QueryList<ElementRef>;

  // dependencies
  public readonly stateManagement = inject(StateManagementService);
  private readonly _userService = inject(UserService);
  private readonly _chatService = inject(ChatService);
  private readonly _messagingService = inject(MessagingService);
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly _storageService = inject(StorageService);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    // get user data
    this._userService.user$.subscribe(user => {
      if (user?.kid) {
        this._kid = user.kid;
      }
    });

    // get token
    this._userService.getToken().then(res => {
      this._isLoggedIn = res !== null;
      if (res !== null) {
        this._token = res as string;

        this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
      }
    });

    // set public chat
    this.setPublicChatForm();

    // set alert state
    effect(() => {
      this.alertState = this.stateManagement.chatAlertState();

      if (this.alertState === AlertStatesEnum.supportChat) {
        // set support chat mode to false
        this.isSupportChatMode = true;
      }
    });

    // start support chat
    this.startChatWithSupport();

    // set business chat data
    effect(() => {
      // get business id
      this._businessId = this.stateManagement.businessId();
      // get business data
      this.businessChatData = this.stateManagement.businessChatData();

      // set business chat data
      if (this._businessId?.length > 0) {
        // remove last messages
        this.stateManagement.allMessages.set([]);

        // fetch support chat data
        // this.setChatStarterAlert();
      }
    });

    // Update suggestion messages on lang change
    this._translateService.onLangChange.subscribe(_ => {
      this.setSuggestionsMessages();
    });

    // set chat loading state
    effect(() => {
      this.isChatLoading = this.stateManagement.isChatLoading();
    });

    // set messages loading state
    effect(() => {
      this.isMessagesLoading = this.stateManagement.isMessagesLoading();
    });

    // remove business id on route change
    this._router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        if (
          (!val.url.includes('products/') || !val.url.includes('wp')) &&
          !val.url.includes('#')
        ) {
          this.stateManagement.businessId.set('');
        }
      }
    });

    // get chat data
    effect(() => {
      // get chat data
      const chat = this.stateManagement.oneChatById();

      if (chat?._id) {
        this._chatId = chat._id;
        this.scrollToBottom();
        this.getAllMessages(this._chatId);
      }
    });

    effect(() => {
      if (this.stateManagement?.allMessagesCount())
        this.messagesCount = this.stateManagement.allMessagesCount() as number;
    });

    effect(() => {
      this.messages = this.stateManagement.allMessages();
      setTimeout(() => {
        this.showPagination = true;
      }, environment.chatPaginationTimeOut);
    });
  }

  ngOnInit(): void {
    // set suggestion messages
    this.setSuggestionsMessages();
  }

  // start chat with support

  public startChatWithSupport(chatId?: string) {
    if (chatId) {
      this._chatService.getChatByChatId(chatId);
      // get messages
      this.scrollToBottom();
      this.getAllMessages(chatId);
    } else {
      this._chatService.createSupportChat();
    }

    // set loading state to true
    this.stateManagement.isMessagesLoading.set(true);
  }

  // get all messages from server

  public fetchDataForNextPage() {
    // set pagination state
    this.showPagination = false;

    // set last page height
    this.lastPageHeight = this.messagesContainer?.nativeElement?.scrollHeight;

    const currentPage = this.stateManagement.messagesCurrentPage();
    const allPagesCount = this.stateManagement.messagesAllPages();

    if (currentPage < allPagesCount) {
      const params = {
        page: currentPage + 1 || 0,
        limit: 20,
      };
      this.getAllMessages(this._chatId, params);
    }
  }

  public getAllMessages(id: string, params?: Params) {
    this._messagingService.getAllMessages(id, params);
  }

  // check if messages changed then scroll to bottom of the chat page
  ngAfterViewInit() {
    this.messageElement?.changes.subscribe(_ => this.scrollToBottom());
  }

  // set chat alert
  public setChatStarterAlert() {
    if (this._isLoggedIn) {
      if (this._businessId && this._businessId?.length > 0) {
        this.alertState = AlertStatesEnum.chatPersonAlert;
      } else {
        // check user already start chat with support or not
        const supportChatAlreadyStarted =
          this._storageService.getStartedSupportChat();
        if (supportChatAlreadyStarted) {
          this.alertState = AlertStatesEnum.supportChat;
        } else {
          this.alertState = AlertStatesEnum.startChatAlert;
        }
      }
    }
  }

  // scroll to bottom functionality
  public scrollToBottom() {
    this.messagesContainer?.nativeElement.scrollTo({
      top:
        this.messages?.length > 0 && !this.showPagination
          ? this.lastPageHeight || 10
          : this.messagesContainer.nativeElement.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
  }

  public closeChatBox() {
    this.stateManagement.isChatBoxOpen.set(false);
  }

  // set suggestion messages
  public setSuggestionsMessages() {
    this.suggestionMessages = [
      this._translateService.instant('chat.suggestionMessages.item1'),
      this._translateService.instant('chat.suggestionMessages.item2'),
      this._translateService.instant('chat.suggestionMessages.item3'),
      this._translateService.instant('chat.suggestionMessages.item4'),
      this._translateService.instant('chat.suggestionMessages.item5'),
    ];
  }

  public setPublicChatForm() {
    const chatId = this._storageService.getChatId();

    if (this._token && !chatId) {
      this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
      this.startChatWithSupport();
    } else if (chatId) {
      this.startChatWithSupport(chatId);
      this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
    } else {
      this.stateManagement.chatAlertState.set(AlertStatesEnum.publicChat);
    }
  }

  ngOnDestroy(): void {
    this.stateManagement.selectedSuggestionMessage.set('');
  }

  protected readonly AlertStatesEnum = AlertStatesEnum;
}
